import { InvoiceTabEnum } from 'containers/invoices/create-invoice/constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useContext, useMemo } from 'react';
import DateUtils from 'utils/dateUtils';
import {
  selectInvoiceOptionsEditPage,
  selectPaymentRemindersEditPage,
  updateDueDateEditPage,
  updatePaymentRemindersEditPage
} from '../invoicesEditSlice';
import { InvoiceContext } from 'containers/invoices/types';
import { DueDateSection } from 'containers/invoices/create-invoice/components/DueDateSection';
import { recalculateReminderDates } from 'containers/invoices/utils';

interface InvoiceOptionsSectionProps {
  className?: string;
  context: React.Context<InvoiceContext>;
}

const InvoiceSection: FC<InvoiceOptionsSectionProps> = ({ context }) => {
  const { selectedInvoiceTab } = useContext<InvoiceContext>(context);
  const dispatch = useAppDispatch();
  const selectedInvoiceOptionsEditPage = useAppSelector(selectInvoiceOptionsEditPage);
  const paymentRemindersEditPage = useAppSelector(selectPaymentRemindersEditPage);
  const dueDate = selectedInvoiceOptionsEditPage.dueDate;
  const invoiceDateSaved = selectedInvoiceOptionsEditPage?.invoiceDate;

  const isTabSelected = selectedInvoiceTab === InvoiceTabEnum.INVOICE_OPTIONS;

  const invoiceDate = useMemo(
    () =>
      invoiceDateSaved ? DateUtils.parseISODateString(invoiceDateSaved) : DateUtils.getStartOfDay(DateUtils.getNow()),
    [invoiceDateSaved]
  );

  const renderInvoiceDate = () => {
    const date = DateUtils.getDateInFormat({ date: invoiceDate, dateFormat: 'dd MMM yyyy' });
    return date;
  };

  const handleDueDateChange = (selectedDate: Date) => {
    const newDueDateISO = selectedDate ? selectedDate.toISOString() : null;
    dispatch(updateDueDateEditPage(newDueDateISO));

    // Recalculate reminder dates if reminders exist
    if (paymentRemindersEditPage.length > 0 && newDueDateISO) {
      const updatedReminders = recalculateReminderDates(paymentRemindersEditPage, newDueDateISO);
      dispatch(updatePaymentRemindersEditPage(updatedReminders));
    }
  };

  // Use the stored due date or fall back to the original due date from invoice options or first predefined option
  const currentDueDate = dueDate ? DateUtils.parseISODateString(dueDate) : invoiceDate;

  return (
    <div>
      <div className={`mb-2 text-xl font-bold text-heading ${isTabSelected ? 'text-primaryBtn' : 'text-heading'}`}>
        Invoice options
      </div>
      <div
        className={`flex items-center justify-between gap-6 rounded-md border py-5 px-[30px]
        1439px:p-10 ${isTabSelected ? 'border-primaryBtn' : 'border-borderGray'}`}>
        <div className="flex flex-row gap-5 text-17px">
          <div className="whitespace-nowrap font-semibold text-primaryText">Invoice date</div>
          <div className="whitespace-nowrap font-semibold text-accentText">{renderInvoiceDate()}</div>
        </div>

        <DueDateSection
          currentDueDate={currentDueDate}
          invoiceDate={invoiceDate}
          onDueDateChange={handleDueDateChange}
        />
      </div>
    </div>
  );
};

export default InvoiceSection;
